import { normalizeCriteriaWeights } from "./decision-engine";
import type { AnalyzeRequest, DecisionReport, Locale, QuestionResponse } from "./types";

export function fallbackQuestions(_decision: string, locale: Locale): QuestionResponse {
  const traditional = locale === "zh-TW";
  return {
    coachIntro: traditional
      ? "先不急著選。四個問題，幫我們找出真正左右這個決定的東西。"
      : "先不急着选。四个问题，帮我们找出真正左右这个决定的东西。",
    questions: traditional
      ? [
          "如果只能保住一件事，對你最重要的結果是什麼？",
          "這個決定不能跨過的底線是什麼？",
          "你最擔心哪個風險？它真的不可逆嗎？",
          "一年後回頭看，做了什麼或沒做什麼會讓你更後悔？",
        ]
      : [
          "如果只能保住一件事，对你最重要的结果是什么？",
          "这个决定不能跨过的底线是什么？",
          "你最担心哪个风险？它真的不可逆吗？",
          "一年后回头看，做了什么或没做什么会让你更后悔？",
        ],
  };
}

export function fallbackReport(input: AnalyzeRequest): DecisionReport {
  const traditional = input.locale === "zh-TW";
  const answers = input.answers.map((item) => item.answer.trim());
  const priority = answers[0] || (traditional ? "長期價值" : "长期价值");
  const constraint = answers[1] || (traditional ? "可承受的代價" : "可承受的代价");
  const risk = answers[2] || (traditional ? "尚未確認的風險" : "尚未确认的风险");
  const regret = answers[3] || (traditional ? "錯過行動時機" : "错过行动时机");

  return {
    version: 1,
    title: input.decision,
    summary: traditional
      ? `你最看重「${priority}」，同時不願越過「${constraint}」這條底線。真正需要補齊的不是更多泛泛建議，而是驗證一個關鍵風險。`
      : `你最看重“${priority}”，同时不愿越过“${constraint}”这条底线。真正需要补齐的不是更多泛泛建议，而是验证一个关键风险。`,
    recommendation: traditional ? "目前先做一個可逆的小步測試" : "目前先做一个可逆的小步测试",
    confidence: "low",
    options: [
      {
        id: "change",
        label: traditional ? "採取改變" : "采取改变",
        scores: { value: 8, safety: 5, regret: 8 },
      },
      {
        id: "stay",
        label: traditional ? "維持現狀" : "维持现状",
        scores: { value: 5, safety: 8, regret: 4 },
      },
    ],
    criteria: normalizeCriteriaWeights([
      { id: "value", label: priority.slice(0, 18), weight: 45 },
      { id: "safety", label: traditional ? "底線與安全感" : "底线与安全感", weight: 30 },
      { id: "regret", label: traditional ? "長期後悔成本" : "长期后悔成本", weight: 25 },
    ]),
    blindSpots: [
      traditional ? "你可能把資訊不足帶來的不安，誤認成選項本身很危險。" : "你可能把信息不足带来的不安，误认成选项本身很危险。",
      traditional ? `你對「${regret}」的在意，可能正在放大短期時間壓力。` : `你对“${regret}”的在意，可能正在放大短期时间压力。`,
    ],
    openQuestions: [risk, traditional ? `怎樣才算沒有越過「${constraint}」？` : `怎样才算没有越过“${constraint}”？`],
    scenarios: {
      optimistic: traditional ? "小步測試證明改變值得投入，而且保留了退路。" : "小步测试证明改变值得投入，而且保留了退路。",
      likely: traditional ? "你會得到一部分新資訊，決定仍不完美，但不再只是憑感覺。" : "你会得到一部分新信息，决定仍不完美，但不再只是凭感觉。",
      downside: traditional ? "測試結果不理想，但代價可控，並及早排除了一個選項。" : "测试结果不理想，但代价可控，并及早排除了一个选项。",
    },
    nextSteps: [
      traditional ? `列出驗證「${risk}」所需的唯一證據。` : `列出验证“${risk}”所需的唯一证据。`,
      traditional ? "找一位了解實情的人，問一個能改變你判斷的問題。" : "找一位了解实情的人，问一个能改变你判断的问题。",
      traditional ? "設一個 48 小時內能完成、可以反悔的小步測試。" : "设一个 48 小时内能完成、可以反悔的小步测试。",
    ],
    coachNote: traditional
      ? "你不需要等到完全不害怕才決定；你需要的是一個即使判斷錯了也承受得起的下一步。"
      : "你不需要等到完全不害怕才决定；你需要的是一个即使判断错了也承受得起的下一步。",
  };
}
